/**
 * @develio/auth — runtime-agnostic exports.
 *
 * Shared types that are safe to import from either server or client code.
 * Server-only helpers live in `./server`; client-only helpers live in `./client`.
 * No code in this file may import from `@clerk/backend` or `@clerk/clerk-react`
 * directly — those are segregated to avoid leaking server deps into the web bundle
 * and vice versa.
 *
 * See ADR-007 (Clerk behind a wrapper).
 */

/**
 * Develio membership roles. Maps to the PRD §2.3 role table.
 *
 * - `owner`     — EDC Executive Director; full admin (billing, users, forms, API keys, all analyses)
 * - `analyst`   — EDC analyst; create/edit/run analyses and intake
 * - `reviewer`  — City Finance Director / board liaison; read-only + comments
 * - `consultant`— Cross-org user; per-org permissions, own RIMS II, form management across clients
 * - `applicant` — External business applicant; submits intake forms, views submission status
 */
export const ROLES = ['owner', 'analyst', 'reviewer', 'consultant', 'applicant'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Hierarchy check: does `actual` satisfy the required role?
 *
 * Owner dominates Analyst dominates Reviewer. Consultant is parallel to Analyst
 * for cross-org scenarios. Applicant is external and never satisfies any internal role.
 */
const ROLE_RANK: Record<Role, number> = {
  owner: 100,
  analyst: 50,
  consultant: 50,
  reviewer: 20,
  applicant: 0,
};

export function roleSatisfies(actual: Role, required: Role): boolean {
  if (required === 'applicant') return true; // anyone can satisfy "applicant-or-better"
  if (actual === 'applicant') return false;
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/**
 * Canonical Clerk → Develio role mapping.
 *
 * Clerk's built-in organization roles are `org:admin` and `org:member`. We extend
 * with custom metadata for finer roles. See the Clerk dashboard → Roles & Permissions
 * to configure custom roles that map onto this enum.
 */
export function clerkRoleToDevelioRole(clerkRole: string | null | undefined): Role {
  if (!clerkRole) return 'analyst';
  const normalized = clerkRole.replace(/^org:/, '').toLowerCase();
  if (isRole(normalized)) return normalized;
  // Clerk defaults
  if (normalized === 'admin') return 'owner';
  if (normalized === 'member') return 'analyst';
  return 'analyst';
}

/**
 * Shared session context shape. Both server and client helpers return this.
 * The orgId is the Develio organization id (prefixed nanoid), NOT the raw Clerk
 * org id — the sync service in apps/api maps Clerk ids to Develio ids.
 */
export interface DevelioSession {
  readonly userId: string;
  readonly clerkUserId: string;
  readonly orgId: string | null;
  readonly clerkOrgId: string | null;
  readonly role: Role | null;
  readonly email: string | null;
}

/**
 * Error thrown by `requireRole()` when the caller lacks sufficient privilege.
 * API route handlers catch this and return a 403.
 */
export class InsufficientRoleError extends Error {
  readonly statusCode = 403;
  constructor(
    readonly required: Role,
    readonly actual: Role | null,
  ) {
    super(
      actual
        ? `Insufficient role: required '${required}', have '${actual}'`
        : `Insufficient role: required '${required}', have none`,
    );
    this.name = 'InsufficientRoleError';
  }
}

/**
 * Error thrown when no authenticated session is present.
 * API route handlers catch this and return a 401.
 */
export class UnauthenticatedError extends Error {
  readonly statusCode = 401;
  constructor(message = 'Unauthenticated') {
    super(message);
    this.name = 'UnauthenticatedError';
  }
}
